/**
 * wasmLoader.js
 * WebAssembly 模組載入與管理工具
 *
 * 提供以下功能：
 *   - 檢查瀏覽器是否支援 WebAssembly
 *   - 從 URL 動態載入並實例化 .wasm 檔案
 *   - 管理已載入的 WASM 模組快取（避免重複載入）
 *   - 讀取 WASM 匯出函式
 */

// 已載入的 WASM 模組快取，key 為 wasmUrl
const wasmCache = {}

/**
 * 檢查目前環境是否支援 WebAssembly
 * @returns {boolean}
 */
export function isWasmSupported() {
  return (
    typeof WebAssembly === 'object' &&
    typeof WebAssembly.instantiate === 'function' &&
    typeof WebAssembly.compile === 'function'
  )
}

/**
 * 從指定 URL 載入並實例化 WebAssembly 模組。
 * 相同 URL 只會載入一次，後續呼叫直接回傳快取結果。
 *
 * @param {string} wasmUrl          - .wasm 檔案的 URL 路徑
 * @param {object} [importObject={}] - 傳入 WASM 的 import 物件（memory、table、外部函式等）
 * @returns {Promise<WebAssembly.Instance>}
 */
export async function loadWasmModule(wasmUrl, importObject = {}) {
  if (!isWasmSupported()) {
    throw new Error('[WASM] 此環境不支援 WebAssembly')
  }

  // 快取命中，直接回傳
  if (wasmCache[wasmUrl]) {
    console.log(`[WASM] 使用快取模組：${wasmUrl}`)
    return wasmCache[wasmUrl]
  }

  console.log(`[WASM] 載入模組：${wasmUrl}`)

  const response = await fetch(wasmUrl)
  if (!response.ok) {
    throw new Error(`[WASM] 無法取得模組（${response.status} ${response.statusText}）：${wasmUrl}`)
  }

  const buffer = await response.arrayBuffer()

  // 優先使用串流實例化（效能較佳），若不支援則退回 buffer 方式
  let instance
  if (typeof WebAssembly.instantiateStreaming === 'function') {
    // 重新 fetch 以取得可串流的 Response（原始 Response 已被 arrayBuffer 消耗）
    const streamResponse = await fetch(wasmUrl)
    const result = await WebAssembly.instantiateStreaming(streamResponse, importObject)
    instance = result.instance
  } else {
    const result = await WebAssembly.instantiate(buffer, importObject)
    instance = result.instance
  }

  console.log('[WASM] 模組載入完成，匯出項目：', Object.keys(instance.exports))
  wasmCache[wasmUrl] = instance
  return instance
}

/**
 * 取得已快取的 WASM 實例，若尚未載入則回傳 null
 * @param {string} wasmUrl
 * @returns {WebAssembly.Instance|null}
 */
export function getWasmInstance(wasmUrl) {
  return wasmCache[wasmUrl] || null
}

/**
 * 取得已快取 WASM 模組的匯出物件，若尚未載入則回傳 null
 * @param {string} wasmUrl
 * @returns {object|null}
 */
export function getWasmExports(wasmUrl) {
  const instance = getWasmInstance(wasmUrl)
  return instance ? instance.exports : null
}

/**
 * 清除指定或全部 WASM 模組快取（可用於重新載入或記憶體釋放）
 * @param {string} [wasmUrl] - 省略則清除所有快取
 */
export function clearWasmCache(wasmUrl) {
  if (wasmUrl) {
    delete wasmCache[wasmUrl]
    console.log(`[WASM] 已清除快取：${wasmUrl}`)
  } else {
    Object.keys(wasmCache).forEach(key => delete wasmCache[key])
    console.log('[WASM] 已清除所有模組快取')
  }
}

/**
 * 取得模型二進位資料，並選擇性地透過 WASM 模組進行處理後回傳 ArrayBuffer。
 *
 * WASM 模組的介面約定（可選）：
 *   exports.allocate(byteLen: i32) → ptr: i32   配置線性記憶體
 *   exports.processModel(ptr: i32, len: i32) → outLen: i32  處理資料並回傳輸出長度
 *   exports.deallocate(ptr: i32, len: i32)      釋放記憶體（可選）
 *   exports.memory: WebAssembly.Memory
 *
 * 若 WASM 模組未載入或不具備上述匯出，則直接回傳原始 ArrayBuffer（直通模式）。
 *
 * @param {string} modelUrl   - GLB / GLTF 模型的 URL
 * @param {string|null} [wasmUrl] - 已載入的 WASM 模組 URL；null 表示不使用 WASM
 * @returns {Promise<ArrayBuffer>}
 */
export async function fetchAndProcessModel(modelUrl, wasmUrl = null) {
  const response = await fetch(modelUrl)
  if (!response.ok) {
    throw new Error(`[WASM] 無法取得模型（${response.status} ${response.statusText}）：${modelUrl}`)
  }
  const buffer = await response.arrayBuffer()

  // 嘗試透過 WASM 處理
  if (wasmUrl) {
    const instance = getWasmInstance(wasmUrl)
    if (
      instance &&
      typeof instance.exports.allocate === 'function' &&
      typeof instance.exports.processModel === 'function' &&
      instance.exports.memory
    ) {
      try {
        const inputView = new Uint8Array(buffer)
        const byteLen = inputView.length

        // 在 WASM 線性記憶體中分配空間並寫入資料
        const ptr = instance.exports.allocate(byteLen)
        new Uint8Array(instance.exports.memory.buffer, ptr, byteLen).set(inputView)

        // WASM 處理，回傳輸出長度
        const outLen = instance.exports.processModel(ptr, byteLen)
        const result = new Uint8Array(instance.exports.memory.buffer, ptr, outLen).slice()

        if (typeof instance.exports.deallocate === 'function') {
          instance.exports.deallocate(ptr, byteLen)
        }

        console.log(`[WASM] 模型處理完成：${byteLen} bytes → ${outLen} bytes`)
        return result.buffer
      } catch (e) {
        console.warn('[WASM] processModel 執行失敗，降級為直通模式：', e)
      }
    }
  }

  console.log(`[WASM] 模型資料取得完成（直通模式）：${buffer.byteLength} bytes`)
  return buffer
}

export default {
  isWasmSupported,
  loadWasmModule,
  getWasmInstance,
  getWasmExports,
  clearWasmCache,
  fetchAndProcessModel,
}
